import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Home, ChevronRight, AlertTriangle } from "lucide-react";
import { getOrderById } from "../api/orderApi";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

// Safely converts a warranty duration value (string or number) to a number
const parseMonths = (val) => {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};

const fmt = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatOrderId = (id) => {
  if (id && id.length === 24) return `ORD-${id.slice(-6).toUpperCase()}`;
  return id;
};

const getDays = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / 86400000);
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function WarrantyRenewalPage() {
  const { orderId, productId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getOrderById(orderId);
        if (res.success && res.data) {
          const ord = res.data.order || res.data;
          setOrder(ord);
          const found = (ord.products || []).find((p) => {
            const pid = p.productId?._id || p.productId?.id || p.productId;
            return String(pid) === String(productId);
          });
          setItem(found || null);
          const opts = found?.productId?.warrantyRenewalOptions || [];
          if (opts.length) setSelected(opts[0]);
        } else {
          setError("Order not found.");
        }
      } catch (e) {
        setError(e.message || "Failed to load.");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, productId]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const product = item?.productId;
  const productName = product?.name || "Product";
  const productImage =
    !imgErr && product?.images?.length ? product.images[0] : null;
  const warrantyStart = item?.warrantyStartDate || order?.createdAt;
  const warrantyEnd = item?.warrantyEndDate;
  const days = getDays(warrantyEnd);
  const isExpired = days !== null && days < 0;
  const isWarnSoon = days !== null && days >= 0 && days <= 30;
  const totalDays =
    warrantyStart && warrantyEnd
      ? Math.ceil((new Date(warrantyEnd) - new Date(warrantyStart)) / 86400000)
      : 0;
  const used = totalDays - (days || 0);
  const pct =
    totalDays > 0 ? Math.min(100, Math.max(0, (used / totalDays) * 100)) : 0;
  const renewalOptions = product?.warrantyRenewalOptions || [];

  // Calculate base date for renewal (max of current expiry vs today)
  const baseDateForRenewal = warrantyEnd
    ? new Date(Math.max(new Date(warrantyEnd).getTime(), Date.now()))
    : new Date();

  const newExpiry = selected
    ? addMonths(baseDateForRenewal, Number(selected.duration))
    : null;

  const handleRenew = () => {
    if (!selected) return;
    navigate("/warranty-checkout", {
      state: {
        renewalMode: true,
        orderId,
        productId,
        renewalDuration: selected.duration,
        renewalPrice: selected.price,
        productName,
        newExpiry: newExpiry?.toISOString(),
        previousWarrantyEndDate: warrantyEnd,
        orderShippingAddress: order?.shippingAddress || null,
        orderContactPhone:
          order?.contactPhone || order?.shippingAddress?.phone || "",
      },
    });
  };

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading warranty details…</p>
        </div>
      </div>
    );

  if (error || !item)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 px-4">
        <div className="bg-white dark:bg-slate-900 p-10 rounded-2xl shadow border border-red-100 dark:border-red-900/50 text-center max-w-sm w-full">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h2 className="font-black text-gray-800 dark:text-white text-lg mb-2">
            Unable to Load
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            {error || "Product not found in this order."}
          </p>
          <button
            onClick={() => navigate("/profile")}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition"
          >
            Back to Profile
          </button>
        </div>
      </div>
    );

  // ── Main ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-950">
      {/* ── Dark-blue header ─────────────────────────────────────────────── */}
      <div className="bg-[#004792] shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-white font-bold text-xl sm:text-2xl tracking-wide uppercase">
            WARRANTY RENEWAL
          </h1>
          <div className="w-8 h-8 rounded-full border border-white flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
        </div>
      </div>

      {/* ── Page body ────────────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Breadcrumb strip */}
        <nav className="flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 flex-wrap pb-2">
          <Link
            to="/"
            className="flex items-center gap-1 hover:text-blue-600 transition text-[#3f4b5f] dark:text-gray-300 dark:hover:text-blue-400"
          >
            <Home className="w-4 h-4" />
          </Link>
          <ChevronRight className="w-3.5 h-3.5 opacity-50" />
          <Link
            to="/profile"
            className="hover:text-blue-600 transition uppercase tracking-wider text-[#3f4b5f] dark:text-gray-300 dark:hover:text-blue-400"
          >
            MY PRODUCTS
          </Link>
          <ChevronRight className="w-3.5 h-3.5 opacity-50" />
          <span className="uppercase tracking-wider text-[#1d273b] dark:text-white">
            WARRANTY RENEWAL
          </span>
        </nav>
        {/* ── Row 1: two cards ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* PRODUCT DETAILS */}
          <div className="rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="border-l-4 border-blue-600 px-4 py-3 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 border border-t-gray-200 border-r-gray-200 rounded-t-xl">
              <p className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                PRODUCT DETAILS
              </p>
            </div>
            <div className="p-5 space-y-4 bg-white dark:bg-slate-900 rounded-b-xl border-t-0 border border-gray-200 dark:border-slate-800 shadow-sm">
              {/* Name + badge */}
              <div>
                <h2 className="font-bold text-gray-900 dark:text-white text-lg leading-snug uppercase mb-2">
                  {productName}
                </h2>
                <span className="inline-block text-xs font-bold text-[#0c4029] dark:text-[#4ade80] bg-[#e6f4ea] dark:bg-[#14532d] px-3 py-1 rounded-full">
                  Certified Refurbished
                </span>
              </div>

              {/* Image + dates side by side */}
              <div className="flex items-center gap-4">
                {/* Dynamic product image */}
                <div className="w-24 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 flex items-center justify-center">
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={productName}
                      className="w-full h-full object-contain"
                      onError={() => setImgErr(true)}
                      loading="lazy"
                    />
                  ) : (
                    <ShieldCheck className="w-8 h-8 text-gray-300" />
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-semibold">
                      Purchase Date:
                    </p>
                    <p className="font-bold text-gray-800 dark:text-gray-200">
                      {fmt(warrantyStart)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-semibold">
                      Order ID:
                    </p>
                    <p className="font-bold text-gray-800 dark:text-gray-200">
                      #{formatOrderId(orderId)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CURRENT WARRANTY STATUS */}
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
            <div className="border-l-4 border-orange-500 px-4 py-3 bg-gray-50 dark:bg-slate-800 border-b border-gray-100 border border-t-gray-200 border-r-gray-200 dark:border-slate-700 rounded-t-xl">
              <p className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                CURRENT WARRANTY STATUS
              </p>
            </div>
            <div className="p-5 space-y-3 bg-white dark:bg-slate-900 rounded-b-xl border-t-0 border border-gray-200 dark:border-slate-800 shadow-sm">
              <div className="grid grid-cols-2 gap-y-3 text-[15px]">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  Warranty Start:
                </span>
                <span className="font-bold text-gray-900 dark:text-white text-right">
                  {fmt(warrantyStart)}
                </span>
                <span className="text-gray-600 dark:text-gray-400 font-medium">Warranty End:</span>
                <span className="font-bold text-gray-900 dark:text-white text-right">
                  {fmt(warrantyEnd)}
                </span>
              </div>

              {/* Progress bar */}
              {totalDays > 0 && (
                <div className="w-full h-2.5 bg-gray-200 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      isExpired
                        ? "bg-red-500"
                        : isWarnSoon
                          ? "bg-orange-400"
                          : "bg-green-500"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}

              {/* Days remaining */}
              {days !== null ? (
                <p
                  className={`font-semibold text-base mt-2 ${
                    isExpired
                      ? "text-[#c51c1c] dark:text-red-400"
                      : isWarnSoon
                        ? "text-[#c51c1c] dark:text-red-400"
                        : "text-green-700 dark:text-green-400"
                  }`}
                >
                  Days Remaining:{" "}
                  <span className="font-bold text-lg">
                    {isExpired ? "Expired" : `${days} days`}
                  </span>
                </p>
              ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-2">
                  No warranty date recorded yet
                </p>
              )}

              {/* Expiring soon warning */}
              {(isWarnSoon || isExpired) && (
                <div className="inline-flex items-center gap-1.5 bg-[#fef1e5] dark:bg-orange-900/30 px-3 py-1.5 rounded text-[#e06a11] dark:text-orange-400 mt-1">
                  <AlertTriangle
                    className="w-4 h-4 flex-shrink-0 text-[#e06a11] dark:text-orange-400"
                  />
                  <span className="text-sm font-bold">
                    {isExpired ? "Warranty expired!" : "Expiring soon!"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── RENEW WARRANTY card ───────────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <p className="font-bold text-gray-900 dark:text-white uppercase tracking-wide">
              RENEW WARRANTY
            </p>
          </div>

          <div className="p-5">
            {renewalOptions.length === 0 ? (
              <div className="text-center py-10">
                <ShieldCheck className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="font-bold text-gray-500 dark:text-gray-400">
                  No renewal plans available
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Please contact support for assistance.
                </p>
              </div>
            ) : (
              <>
                <p className="font-bold text-gray-800 dark:text-gray-200 mb-4 text-sm">
                  Select Renewal Period:
                </p>

                {/* Option cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  {renewalOptions.map((opt, i) => {
                    const isSel = selected?.duration === opt.duration;
                    const optExpiry = warrantyEnd
                      ? addMonths(
                          new Date(warrantyEnd),
                          parseMonths(opt.duration),
                        )
                      : null;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelected(opt)}
                        className={`text-left p-4 rounded border transition-all ${
                          isSel
                            ? "border-blue-500 bg-[#f0f6ff] dark:bg-blue-900/30"
                            : "bg-[#f7f9fc] dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500"
                        }`}
                      >
                        {/* Radio + label */}
                        <div className="flex items-center gap-2.5 mb-2.5">
                          <div
                            className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${
                              isSel ? "border-blue-600" : "border-gray-400 dark:border-gray-500"
                            }`}
                          >
                            {isSel && (
                              <div className="w-2 h-2 rounded-full bg-blue-600" />
                            )}
                          </div>
                          <span className="font-bold text-[15px] text-gray-900 dark:text-white">
                            {opt.duration} Month Extension
                          </span>
                        </div>
                        <div className="pl-6 space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                          {optExpiry && (
                            <p>
                              → New Expiry:{" "}
                              <strong className="text-gray-900 dark:text-white">
                                {fmt(optExpiry)}
                              </strong>
                            </p>
                          )}
                          <p>
                            → Price:{" "}
                            <strong className="text-green-700 dark:text-green-400 text-[15px]">
                              ₹{Number(opt.price).toLocaleString("en-IN")}
                            </strong>
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Renew Now button — centered, fixed width like reference */}
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleRenew}
                    disabled={!selected}
                    className={`flex items-center justify-center gap-2 px-14 py-3 rounded font-bold text-white uppercase tracking-wide text-sm transition-all ${
                      selected
                        ? "bg-[#0052cc] hover:bg-blue-700 shadow shadow-blue-200"
                        : "bg-[#0052cc] opacity-50 cursor-not-allowed"
                    }`}
                  >
                    RENEW NOW
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
